import React, { useState, useEffect } from 'react'
import { budgetDashboardService } from '../lib/api'
import { formatRupees } from '../utils/currency'
import { Users, Share, Plus, X, IndianRupee, TrendingUp, TrendingDown } from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

const BudgetDashboardPage = () => {
  const [dashboardData, setDashboardData] = useState({
    total_budget: 0,
    users: []
  })
  const [loading, setLoading] = useState(true)
  const [showShareModal, setShowShareModal] = useState(false)
  const [shareEmail, setShareEmail] = useState('')
  const [shareLoading, setShareLoading] = useState(false)
  const [myShares, setMyShares] = useState([])

  useEffect(() => {
    fetchDashboardData()
    fetchMyShares()
  }, [])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const data = await budgetDashboardService.getBudgetDashboard()
      setDashboardData(data)
    } catch (error) {
      console.error('Error fetching budget dashboard:', error)
      toast.error('Failed to load budget dashboard')
    } finally {
      setLoading(false)
    }
  }

  const fetchMyShares = async () => {
    try {
      const shares = await budgetDashboardService.getMyShares()
      setMyShares(shares)
    } catch (error) {
      console.error('Error fetching my shares:', error)
    }
  }

  const handleShareBudget = async (e) => {
    e.preventDefault()
    if (!shareEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }

    try {
      setShareLoading(true)
      await budgetDashboardService.shareBudget(shareEmail.trim())
      toast.success(`Budget shared with ${shareEmail}`)
      setShareEmail('')
      setShowShareModal(false)
      fetchDashboardData()
      fetchMyShares()
    } catch (error) {
      console.error('Error sharing budget:', error)
      toast.error(error.message || 'Failed to share budget')
    } finally {
      setShareLoading(false)
    }
  }

  const handleUnshareBudget = async (email) => {
    try {
      await budgetDashboardService.unshareBudget(email)
      toast.success(`Budget sharing removed with ${email}`)
      fetchDashboardData()
      fetchMyShares()
    } catch (error) {
      console.error('Error unsharing budget:', error)
      toast.error('Failed to remove budget sharing')
    }
  }

  const getStatusIcon = (color) => {
    switch (color) {
      case 'green':
        return <TrendingUp className="h-5 w-5 text-green-600" />
      case 'yellow':
        return <TrendingUp className="h-5 w-5 text-yellow-600" />
      case 'red':
        return <TrendingDown className="h-5 w-5 text-red-600" />
      default:
        return <IndianRupee className="h-5 w-5 text-gray-600" />
    }
  }

  const getStatusColor = (color) => {
    switch (color) {
      case 'green':
        return 'bg-green-100 dark:bg-green-900 border-green-200 dark:border-green-700'
      case 'yellow':
        return 'bg-yellow-100 dark:bg-yellow-900 border-yellow-200 dark:border-yellow-700'
      case 'red':
        return 'bg-red-100 dark:bg-red-900 border-red-200 dark:border-red-700'
      default:
        return 'bg-gray-100 dark:bg-gray-900 border-gray-200 dark:border-gray-700'
    }
  }

  const getProgressBarColor = (color) => {
    switch (color) {
      case 'green':
        return 'bg-green-500'
      case 'yellow':
        return 'bg-yellow-500'
      case 'red':
        return 'bg-red-500'
      default:
        return 'bg-gray-500'
    }
  }

  if (loading) {
    return <LoadingSpinner message="Loading budget dashboard..." />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Budget Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Monitor your total budget and shared budgets with others
          </p>
        </div>
        
        <button
          onClick={() => setShowShareModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Share className="h-4 w-4" />
          <span>Share Budget</span>
        </button>
      </div>

      {/* Total Budget Card */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Total Combined Budget
            </h2>
            <p className="text-4xl font-bold text-primary-600 dark:text-primary-400">
              {formatRupees(dashboardData.total_budget)}
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Including {dashboardData.users.length} budget{dashboardData.users.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="hidden sm:block">
            <Users className="h-16 w-16 text-primary-600 dark:text-primary-400 opacity-20" />
          </div>
        </div>
      </div>

      {/* Budget Breakdown */}
      {dashboardData.users.length > 0 ? (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            Budget Breakdown by User
          </h3>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {dashboardData.users.map((user) => {
              const progressPercentage = user.budget > 0 ? Math.max(0, (user.left / user.budget) * 100) : 0
              
              return (
                <div
                  key={user.user_id}
                  className={`p-4 rounded-lg border-2 ${getStatusColor(user.color)} transition-all hover:shadow-md`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">
                          {user.name}
                          {user.is_self && (
                            <span className="ml-2 text-xs bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 px-2 py-1 rounded-full">
                              You
                            </span>
                          )}
                        </h4>
                      </div>
                    </div>
                    {getStatusIcon(user.color)}
                  </div>

                  {/* Budget Stats */}
                  <div className="space-y-2 mb-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Budget:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatRupees(user.budget)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Spent:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">
                        {formatRupees(user.spent)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Left:</span>
                      <span className={`font-semibold ${
                        user.left >= 0 
                          ? 'text-green-600 dark:text-green-400' 
                          : 'text-red-600 dark:text-red-400'
                      }`}>
                        {formatRupees(user.left)}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all duration-300 ${getProgressBarColor(user.color)}`}
                      style={{ width: `${Math.min(100, progressPercentage)}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {progressPercentage.toFixed(1)}% budget remaining
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div className="card text-center py-12">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            No Shared Budgets
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            No budgets are currently shared with you. Ask someone to share their budget or create your own budget first.
          </p>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 max-w-md mx-auto">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="text-left">
                <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                  How to get started:
                </h4>
                <ul className="mt-2 text-sm text-blue-700 dark:text-blue-300 space-y-1">
                  <li>• Create budgets in the main Budgets page</li>
                  <li>• Share your budget using the "Share Budget" button</li>
                  <li>• Ask others to share their budgets with you</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* My Shares Section */}
      {myShares.length > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Budget Shared With
          </h3>
          <div className="space-y-2">
            {myShares.map((share) => (
              <div
                key={share.user_id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {share.name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {share.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {share.email}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleUnshareBudget(share.email)}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 p-1"
                  title="Remove sharing"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Share Budget Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Share Your Budget
              </h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleShareBudget} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={shareEmail}
                  onChange={(e) => setShareEmail(e.target.value)}
                  className="input"
                  placeholder="user@example.com"
                  required
                />
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Enter the email of the user you want to share your budget with.
                </p>
              </div>
              
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowShareModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={shareLoading}
                  className="btn-primary flex-1"
                >
                  {shareLoading ? 'Sharing...' : 'Share Budget'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default BudgetDashboardPage
